/**
 * The agency's books checked against each other (shared/balances), and the
 * one correction they offer: taking back commission booked on something that
 * no longer earns it.
 *
 * Takes the PocketBase session it reads and writes with, so the Money page
 * (the signed-in agent's session, api/balances.ts) and the Claude connector
 * (the same person's, api/_needsYou.ts) run one rule under one set of row
 * rules. Computed from the records each time; nothing is stored.
 */

import type PocketBase from 'pocketbase'
import { checkBalances, type BalanceFinding } from './balances.js'
import { FEE_INCOME_TYPES } from './feeTypes.js'

export interface Balances {
  findings: BalanceFinding[]
  /** What was checked, so "nothing found" is visibly about something. */
  checked: { ledger_rows: number; signed_deals: number; fee_payments: number }
}

/**
 * Takes back commission booked on a contract or deal that no longer earns it.
 *
 * "Fees booked" counts the ledger's commission_earned rows, so a separate
 * negative adjustment would leave the figure wrong. The row is set to nothing
 * instead, and keeps its history: the amounts it held, why it was reversed,
 * who did it and when are written into its notes. Refused when expenses were
 * recouped against the row — undoing those means un-marking the expenses,
 * which is not something to do silently — and re-checked against its source,
 * so a row whose contract was reopened is never zeroed.
 */
export async function reverseBookedCommission(pb: PocketBase, rowId: string, userId: string): Promise<{ reversed: number }> {
  const row = await pb.collection('commission_ledger').getOne<Record<string, any>>(rowId)
  if (row.transaction_type !== 'commission_earned') throw new Error('Only booked commission can be reversed here.')
  const gross = Number(row.gross_amount ?? 0)
  if (!(gross > 0)) throw new Error('Nothing is booked on this row.')
  if (Number(row.expenses_deducted ?? 0) > 0) {
    throw new Error(`$${Number(row.expenses_deducted).toLocaleString('en-US')} of expenses were recouped against this row. Reverse it from the commission ledger so the expenses are released too.`)
  }
  let source = ''
  if (row.contract_id) {
    const c = await pb.collection('contracts').getOne<Record<string, any>>(row.contract_id, { fields: 'status,title' }).catch((err: any) => (err?.status === 404 ? null : Promise.reject(err)))
    if (c && c.status === 'active') throw new Error('The contract is active again; its commission stands.')
    source = c ? `${c.title || 'the contract'} is ${c.status}` : 'the contract no longer exists'
  } else if (row.deal_id) {
    const d = await pb.collection('deals').getOne<Record<string, any>>(row.deal_id, { fields: 'stage,title' }).catch((err: any) => (err?.status === 404 ? null : Promise.reject(err)))
    if (d && d.stage === 'Executed') throw new Error('The deal is signed; its commission stands.')
    source = d ? `${d.title || 'the deal'} is at ${d.stage || 'no stage'}` : 'the deal no longer exists'
  } else {
    throw new Error('This row is not tied to a contract or deal, so there is nothing to check it against.')
  }
  const day = new Date().toISOString().slice(0, 10)
  const was = `gross $${gross.toLocaleString('en-US')}, net $${Number(row.net_amount ?? 0).toLocaleString('en-US')}, agent $${Number(row.agent_share ?? 0).toLocaleString('en-US')}, agency $${Number(row.agency_share ?? 0).toLocaleString('en-US')}`
  await pb.collection('commission_ledger').update(rowId, {
    gross_amount: 0, net_amount: 0, agent_share: 0, agency_share: 0,
    notes: `${row.notes ? `${row.notes} ` : ''}Reversed on ${day} by ${userId}: ${source}, so it no longer earns commission. Was ${was}.`,
  })
  return { reversed: gross }
}

/** @param agency A ready filter on agency_id, escaped by the caller. */
export async function loadBalances(pb: PocketBase, agency: string): Promise<Balances> {
  const all = (collection: string, filter: string, fields: string) => pb.collection(collection).getFullList<Record<string, any>>({ filter, fields })
  const feeTypes = [...FEE_INCOME_TYPES].map(t => `type = "${t}"`).join(' || ')
  const [ledger, contracts, deals, dealIncome, fees, clients] = await Promise.all([
    all('commission_ledger', agency, 'id,transaction_type,gross_amount,expenses_deducted,net_amount,agent_share,agency_share,contract_id,deal_id,client_id'),
    all('contracts', agency, 'id,title,status,client_id'),
    all('deals', agency, 'id,title,stage,value,commission_rate,commission_amount,payment_schedule,client_id'),
    all('transactions', `${agency} && type = "athlete_income" && deal_id != ""`, 'deal_id,amount'),
    all('transactions', `${agency} && (${feeTypes})`, 'client_id,amount'),
    all('clients', agency, 'id,full_name,first_name,last_name'),
  ])
  const names = new Map(clients.map(c => [c.id, c.full_name || [c.first_name, c.last_name].filter(Boolean).join(' ')]))
  return {
    findings: checkBalances({ ledger: ledger as any, contracts: contracts as any, deals: deals as any, dealIncome: dealIncome as any, fees: fees as any, names }),
    checked: {
      ledger_rows: ledger.filter(r => r.transaction_type === 'commission_earned').length,
      signed_deals: deals.filter(d => d.stage === 'Executed').length,
      fee_payments: fees.length,
    },
  }
}
